import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus, Trash, FloppyDisk, PencilSimple,
  UsersThree, UserMinus, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export function AudiencesTab() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [savingNew, setSavingNew] = useState(false);
  const [activeList, setActiveList] = useState(null);
  const [memberDetails, setMemberDetails] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameName, setRenameName] = useState("");
  const searchTimer = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/audience-lists")
      .then(({ data }) => setLists(data))
      .catch(() => toast.error("Failed to load audience lists"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openList = async (list) => {
    setActiveList(list);
    setMemberSearch("");
    setSearchQuery("");
    setSearchResults([]);
    const emails = list.member_emails || [];
    if (!emails.length) { setMemberDetails([]); return; }
    try {
      const results = await Promise.all(
        emails.map((email) =>
          api.get(`/recipients?q=${encodeURIComponent(email)}&limit=5`)
            .then(({ data }) => {
              const match = (data.items || data).find(
                (r) => (r.recipient_email || "").toLowerCase() === email.toLowerCase()
              );
              return match
                ? { email, name: match.recipient_name, customer_number: match.customer_number }
                : { email, name: email, customer_number: null };
            })
            .catch(() => ({ email, name: email, customer_number: null }))
        )
      );
      setMemberDetails(results);
    } catch {
      setMemberDetails(emails.map((e) => ({ email: e, name: e, customer_number: null })));
    }
  };

  const createList = async () => {
    if (!newName.trim()) return;
    setSavingNew(true);
    try {
      const { data } = await api.post("/audience-lists", { name: newName.trim() });
      setLists((prev) => [data, ...prev]);
      setNewName("");
      setCreating(false);
      toast.success("Audience list created");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to create list");
    } finally {
      setSavingNew(false);
    }
  };

  const deleteList = async (lid) => {
    if (!window.confirm("Delete this audience list?")) return;
    try {
      await api.delete(`/audience-lists/${lid}`);
      setLists((prev) => prev.filter((l) => l.id !== lid));
      if (activeList?.id === lid) setActiveList(null);
      toast.success("List deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to delete");
    }
  };

  const saveRename = async (lid) => {
    if (!renameName.trim()) return;
    try {
      const { data } = await api.patch(`/audience-lists/${lid}`, { name: renameName.trim() });
      setLists((prev) => prev.map((l) => l.id === lid ? { ...l, name: data.name } : l));
      if (activeList?.id === lid) setActiveList((prev) => ({ ...prev, name: data.name }));
      setRenameId(null);
      toast.success("Renamed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to rename");
    }
  };

  const removeMember = async (email) => {
    if (!activeList) return;
    try {
      const { data } = await api.delete(`/audience-lists/${activeList.id}/members/${encodeURIComponent(email)}`);
      const updatedList = { ...activeList, member_emails: data.member_emails, member_count: data.member_count };
      setActiveList(updatedList);
      setLists((prev) => prev.map((l) => l.id === activeList.id ? { ...l, member_count: data.member_count } : l));
      setMemberDetails((prev) => prev.filter((m) => m.email.toLowerCase() !== email.toLowerCase()));
      toast.success("Member removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to remove");
    }
  };

  const addMember = async (email, name) => {
    if (!activeList) return;
    if ((activeList.member_emails || []).map((e) => e.toLowerCase()).includes(email.toLowerCase())) {
      toast.info("Already in this list");
      return;
    }
    try {
      const { data } = await api.post(`/audience-lists/${activeList.id}/members`, { emails: [email] });
      const updatedList = { ...activeList, member_emails: data.member_emails, member_count: data.member_count };
      setActiveList(updatedList);
      setLists((prev) => prev.map((l) => l.id === activeList.id ? { ...l, member_count: data.member_count } : l));
      setMemberDetails((prev) => [...prev, { email, name, customer_number: null }]);
      setSearchQuery("");
      setSearchResults([]);
      toast.success(`${name || email} added`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to add");
    }
  };

  useEffect(() => {
    if (!searchQuery.trim() || !activeList) { setSearchResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/recipients?q=${encodeURIComponent(searchQuery)}&limit=30`);
        setSearchResults(data.items || data || []);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [searchQuery, activeList]);

  const filtered = memberDetails.filter((m) =>
    !memberSearch ||
    m.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <div className="flex gap-5 h-full min-h-[520px]">

      {/* ── Left: list panel ─────────────────────────────────────────── */}
      <div className="w-72 shrink-0 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-800">Audience Lists</h3>
          <Button size="sm" variant="outline" className="text-xs gap-1.5 h-7" onClick={() => setCreating(true)} data-testid="new-audience-list-btn">
            <Plus size={12} /> New list
          </Button>
        </div>

        {creating && (
          <div className="flex gap-2 items-center">
            <Input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="List name…"
              className="h-8 text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter") createList();
                if (e.key === "Escape") { setCreating(false); setNewName(""); }
              }}
              data-testid="new-audience-name-input"
            />
            <Button size="sm" className="h-8 px-2.5" onClick={createList} disabled={savingNew} data-testid="save-audience-list-btn">
              <FloppyDisk size={13} />
            </Button>
          </div>
        )}

        {loading ? (
          <p className="text-xs text-zinc-400 py-4 text-center">Loading…</p>
        ) : lists.length === 0 ? (
          <p className="text-xs text-zinc-400 py-4 text-center">No lists yet. Create one to get started.</p>
        ) : (
          <div className="space-y-1.5 overflow-y-auto">
            {lists.map((list) => (
              <div
                key={list.id}
                onClick={() => openList(list)}
                className={`group flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer border transition-all ${
                  activeList?.id === list.id
                    ? "border-zinc-900 bg-zinc-900 text-white"
                    : "border-zinc-200 bg-white hover:border-zinc-300"
                }`}
                data-testid={`audience-list-item-${list.id}`}
              >
                <div className="flex-1 min-w-0">
                  {renameId === list.id ? (
                    <input
                      autoFocus
                      value={renameName}
                      onChange={(e) => setRenameName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(list.id);
                        if (e.key === "Escape") setRenameId(null);
                      }}
                      onBlur={() => saveRename(list.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm w-full bg-transparent border-b border-zinc-500 outline-none"
                    />
                  ) : (
                    <p className="text-sm font-medium truncate">{list.name}</p>
                  )}
                  <p className={`text-xs mt-0.5 ${activeList?.id === list.id ? "text-zinc-400" : "text-zinc-500"}`}>
                    {list.member_count} {list.member_count === 1 ? "member" : "members"}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="p-1 rounded hover:bg-zinc-200 hover:text-zinc-700"
                    title="Rename"
                    onClick={() => { setRenameId(list.id); setRenameName(list.name); }}
                    data-testid={`rename-list-${list.id}`}
                  >
                    <PencilSimple size={12} />
                  </button>
                  <button
                    className="p-1 rounded hover:bg-red-100 hover:text-red-600"
                    title="Delete list"
                    onClick={() => deleteList(list.id)}
                    data-testid={`delete-list-${list.id}`}
                  >
                    <Trash size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Right: member panel ──────────────────────────────────────── */}
      <div className="flex-1 border border-zinc-200 rounded-xl overflow-hidden flex flex-col">
        {!activeList ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-zinc-400">
            <UsersThree size={32} />
            <p className="text-sm">Select a list to manage members</p>
          </div>
        ) : (
          <>
            <div className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-zinc-800">{activeList.name}</h3>
                <p className="text-xs text-zinc-500">{activeList.member_count} {activeList.member_count === 1 ? "member" : "members"}</p>
              </div>
              <div className="relative w-64">
                <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search customers to add…"
                  className="!pl-9 h-8 text-xs"
                  data-testid="add-member-search"
                />
                {searchQuery && (
                  <button className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" onClick={() => { setSearchQuery(""); setSearchResults([]); }}>
                    <X size={12} />
                  </button>
                )}
                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg z-20 overflow-hidden max-h-52 overflow-y-auto">
                    {searching && <p className="text-xs text-zinc-400 px-3 py-2">Searching…</p>}
                    {searchResults.map((r) => {
                      const alreadyIn = (activeList.member_emails || []).map((e) => e.toLowerCase()).includes((r.recipient_email || "").toLowerCase());
                      return (
                        <button
                          key={r.id || r.recipient_email}
                          disabled={alreadyIn}
                          onClick={() => addMember(r.recipient_email, r.recipient_name)}
                          className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors ${alreadyIn ? "opacity-40 cursor-not-allowed" : ""}`}
                          data-testid={`search-result-${r.recipient_email}`}
                        >
                          <div>
                            <p className="text-xs font-medium text-zinc-800">{r.recipient_name}</p>
                            <p className="text-xs text-zinc-500">{r.recipient_email}</p>
                          </div>
                          {alreadyIn ? <span className="text-xs text-zinc-400">Added</span> : <Plus size={12} className="text-zinc-400 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="px-5 py-2.5 border-b border-zinc-100">
              <div className="relative">
                <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                <Input
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Filter members…"
                  className="!pl-9 h-7 text-xs"
                  data-testid="filter-members-input"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-zinc-400">
                  <UserMinus size={28} />
                  <p className="text-sm">
                    {activeList.member_count === 0
                      ? "No members yet — search to add customers"
                      : "No members match your filter"}
                  </p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-100">
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Name</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Email</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Customer #</th>
                      <th className="px-5 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((m) => (
                      <tr key={m.email} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors group">
                        <td className="px-5 py-3 text-zinc-800 font-medium">{m.name || "—"}</td>
                        <td className="px-5 py-3 text-zinc-600">{m.email}</td>
                        <td className="px-5 py-3 text-zinc-500 text-xs">{m.customer_number || "—"}</td>
                        <td className="px-5 py-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            onClick={() => removeMember(m.email)}
                            data-testid={`remove-member-${m.email}`}
                            title="Remove from list"
                          >
                            <Trash size={13} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
